import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Container,
  Paper,
  Grid,
} from "@mui/material";
import Mic from "@mui/icons-material/Mic";
import Close from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import Vapi from "@vapi-ai/web";

// Initialize Vapi with the Public Key
const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);

// Google Apps Script URL for form submission
const GAS_URL =
  "https://script.google.com/macros/s/AKfycbx2fKm1EpiWyfaX__ja7al7ICSjhtdZTK1b7FJM10t77alEDT5DqnWnNK4MLS_kvt4t/exec";

// JSONP Utility
function jsonp(url, params = {}, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    const cbName = "cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("JSONP timeout"));
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeout);
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cbName] = (data) => {
      cleanup();
      resolve(data);
    };

    const qs = new URLSearchParams({ ...params, callback: cbName }).toString();
    script.src = `${url}?${qs}`;
    script.onerror = () => {
      cleanup();
      reject(new Error("JSONP network error"));
    };
    document.head.appendChild(script);
  });
}

// AI Agents Data
const aiAgents = [
  {
    id: "dane-csr",
    name: "Dane",
    role: "Service Receptionist",
    description: "I book jobs, dispatch crews, and handle emergencies.",
    assistantId:
      process.env.REACT_APP_DANE_ASSISTANT_ID ||
      process.env.REACT_APP_VAPI_NOISE_ASSISTANT_ID ||
      "d4a9f0e8-1234-5678-9abc-def012345678",
    color: "#E2684A",
  },
  {
    id: "amy-receptionist",
    name: "Amy",
    role: "Front Desk",
    description: "I greet customers, route calls, and block spam.",
    assistantId:
      process.env.REACT_APP_AMY_ASSISTANT_ID ||
      process.env.REACT_APP_VAPI_LUNAZONE_ASSISTANT_ID ||
      "a8b7c6d5-4321-8765-9cba-fed210987654",
    color: "#333333",
  },
  {
    id: "mark-happy-calls",
    name: "Mark",
    role: "Success Manager",
    description: "I follow up to ensure satisfaction and drive retention.",
    assistantId:
      process.env.REACT_APP_MARK_ASSISTANT_ID ||
      process.env.REACT_APP_VAPI_HDFC_ASSISTANT_ID ||
      "b9c8d7e6-5432-9876-0fed-cba098765432",
    color: "#333333",
  },
];

const AgentCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(3),
  borderRadius: "14px",
  background: "#0F0728",
  border: "1px solid #1E2130",
  color: "#E7E9EE",
  transition: "border-color 0.2s ease, transform 0.2s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-3px)",
    border: `1px solid ${color || "#E2684A"}`,
  },
}));

const DemoSection = () => {
  const [activeAgent, setActiveAgent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userDetails, setUserDetails] = useState({ name: "", email: "" });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    vapi.on("call-start", () => setIsVoiceActive(true));
    vapi.on("call-end", () => setIsVoiceActive(false));
    vapi.on("error", (e) => {
      console.error(e);
      setIsVoiceActive(false);
    });

    return () => vapi.removeAllListeners();
  }, []);

  const handleAgentClick = (agent) => {
    setActiveAgent(agent);
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Save data via JSONP
      await jsonp(GAS_URL, {
        action: "saveUserData",
        email: userDetails.email,
        assistantName: activeAgent?.name || "Unknown",
        pageUrl: window.location.href,
        timestamp: new Date().toISOString(),
      });
      setFormSubmitted(true);
    } catch (err) {
      console.error("Form submission error", err);
      // Allow proceeding even if saving fails for demo purpose
      setFormSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleVoice = () => {
    if (isVoiceActive) {
      vapi.stop();
    } else {
      vapi.start(activeAgent.assistantId);
    }
  };

  return (
    <Box sx={{ py: { xs: 10, md: 13 }, position: "relative", borderBottom: "1px solid #1E2130", background: "#030014" }} id="demo">
      <Container maxWidth="lg">
        <Typography sx={{
          fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
          fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase",
          color: "#E2684A", mb: 1.75,
        }}>
          Live Demo · AI Voice Agents
        </Typography>
        <Typography sx={{
          fontFamily: "'IBM Plex Serif', serif",
          fontSize: { xs: "1.75rem", md: "2.5rem" },
          fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.15,
          color: "#E7E9EE", maxWidth: 680, mb: 2,
        }}>
          Experience Our Voice Technology
        </Typography>
        <Typography sx={{ color: "#B6BBC6", mb: 6, maxWidth: "580px", fontSize: "15px", lineHeight: 1.7 }}>
          Talk to our AI agents in real-time. Experience sub-500ms latency and human-like understanding.
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          {aiAgents.map((agent) => (
            <Grid item xs={12} md={4} key={agent.id}>
              <AgentCard
                color={agent.color}
                onClick={() => handleAgentClick(agent)}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: "50%",
                    bgcolor: agent.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    fontSize: "24px",
                  }}
                >
                  {agent.name.charAt(0)}
                </Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  {agent.name}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: "rgba(255,255,255,0.5)", mb: 2 }}>
                  {agent.role}
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {agent.description}
                </Typography>
              </AgentCard>
            </Grid>
          ))}
        </Grid>

        {/* Overlay Form/Demo */}
        {showForm && activeAgent && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(0,0,0,0.8)",
              backdropFilter: "blur(10px)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <Paper
              sx={{
                width: "100%",
                maxWidth: 400,
                p: 4,
                borderRadius: "14px",
                bgcolor: "#0F0728",
                border: "1px solid #1E2130",
                color: "#E7E9EE",
                position: "relative",
              }}
            >
              <Close
                onClick={() => {
                  setShowForm(false);
                  vapi.stop();
                  setFormSubmitted(false);
                  setUserDetails({ name: "", email: "" });
                }}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  cursor: "pointer",
                  color: "rgba(255,255,255,0.5)",
                }}
              />

              {!formSubmitted ? (
                <Box component="form" onSubmit={handleFormSubmit}>
                  <Typography variant="h5" fontWeight="bold" mb={1}>
                    Try {activeAgent.name}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.6)" mb={4}>
                    Enter your details to start the session.
                  </Typography>

                  <TextField
                    fullWidth
                    label="Name"
                    variant="outlined"
                    value={userDetails.name}
                    onChange={(e) =>
                      setUserDetails({ ...userDetails, name: e.target.value })
                    }
                    sx={{
                      mb: 2,
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                        "&:hover fieldset": { borderColor: "#E2684A" },
                        "&.Mui-focused fieldset": { borderColor: "#E2684A" },
                      },
                      "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#E2684A" },
                    }}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    variant="outlined"
                    value={userDetails.email}
                    onChange={(e) =>
                      setUserDetails({ ...userDetails, email: e.target.value })
                    }
                    sx={{
                      mb: 4,
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        "& fieldset": { borderColor: "rgba(255,255,255,0.2)" },
                        "&:hover fieldset": { borderColor: "#E2684A" },
                        "&.Mui-focused fieldset": { borderColor: "#E2684A" },
                      },
                      "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.5)" },
                      "& .MuiInputLabel-root.Mui-focused": { color: "#E2684A" },
                    }}
                    required
                  />

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: "#E2684A",
                      color: "#fff",
                      py: 1.5,
                      borderRadius: "12px",
                      fontWeight: "bold",
                      "&:hover": { bgcolor: "#c04e32" },
                    }}
                  >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Start Demo"}
                  </Button>
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Box
                    onClick={toggleVoice}
                    component={motion.div}
                    animate={
                      isVoiceActive
                        ? { scale: [1, 1.1, 1], boxShadow: "0 0 30px #E2684A" }
                        : {}
                    }
                    transition={{ repeat: Infinity, duration: 2 }}
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      bgcolor: isVoiceActive ? "#E2684A" : "rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      cursor: "pointer",
                      border: "2px solid rgba(255,255,255,0.1)",
                    }}
                  >
                    <Mic sx={{ fontSize: 40, color: "#fff" }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" mt={4}>
                    {isVoiceActive ? "Listening..." : "Tap to Speak"}
                  </Typography>
                  <Typography variant="body2" color="rgba(255,255,255,0.5)" mt={1}>
                    {isVoiceActive
                      ? "Say anything to test the agent."
                      : "Click the mic to start the conversation."}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default DemoSection;
